import React, { useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "./GoalButton";
import GoalPill from "./GoalPill";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import goalSelectStyles from "./goalSelectStyles";
import { MONTH_LABELS, percentageSum, stripNegative, noSpinnerSx } from "../goals.constants";

const emptyForm = {
  label: "",
  from_month: 1,
  to_month: 3,
  client_satisfaction_percentage: "",
  professional_goals_percentage: "",
  personal_goals_percentage: "",
  sort_order: 0,
};

const formFromTemplate = (template) => ({
  label: template.label,
  from_month: template.from_month,
  to_month: template.to_month,
  client_satisfaction_percentage: template.client_satisfaction_percentage,
  professional_goals_percentage: template.professional_goals_percentage,
  personal_goals_percentage: template.personal_goals_percentage,
  sort_order: template.sort_order,
});

// Add / edit form for a goal period template, shown inline on the templates
// screen. Pass `template` to edit, omit it to add a new one. With `isCopy`,
// `template` only pre-fills a new template (label gets " (Copy)"). `onSave(body)`
// resolves truthy on success.
function GoalTemplateForm({ template, isCopy, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    if (!template) return emptyForm;
    const values = formFromTemplate(template);
    return isCopy ? { ...values, label: `${values.label} (Copy)` } : values;
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);

  const totalPercentage = percentageSum([
    form.client_satisfaction_percentage,
    form.professional_goals_percentage,
    form.personal_goals_percentage,
  ]);

  const handleSave = async () => {
    if (!form.label.trim()) {
      setLocalError("Label is required.");
      return;
    }
    if (Number(form.to_month) < Number(form.from_month)) {
      setLocalError("To month must be on or after from month.");
      return;
    }
    setLocalError(null);

    const body = {
      label: form.label.trim(),
      from_month: Number(form.from_month),
      to_month: Number(form.to_month),
      total_percentage: totalPercentage,
      client_satisfaction_percentage: Number(form.client_satisfaction_percentage || 0),
      professional_goals_percentage: Number(form.professional_goals_percentage || 0),
      personal_goals_percentage: Number(form.personal_goals_percentage || 0),
      sort_order: Number(form.sort_order || 0),
    };

    setIsSubmitting(true);
    await onSave(body);
    setIsSubmitting(false);
  };

  const monthOptions = MONTH_LABELS.map((label, index) => ({ value: index + 1, label }));

  const monthSelect = (field) => (
    <CustomSelect
      customStyles={goalSelectStyles}
      options={monthOptions}
      value={monthOptions[form[field] - 1]}
      onChange={(option) => setForm({ ...form, [field]: option.value })}
      menuPortalTarget={document.body}
    />
  );

  const percentageInput = (field, label) => (
    <Grid item xs={12} md={4}>
      <SuiTypography variant="caption">{label}</SuiTypography>
      <SuiInput
        type="number"
        sx={noSpinnerSx}
        inputProps={{ min: 0, onWheel: (e) => e.target.blur() }}
        value={form[field]}
        onChange={(e) => setForm({ ...form, [field]: stripNegative(e.target.value) })}
      />
    </Grid>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <SuiBox p={3}>
        <SuiTypography variant="h6" fontWeight="bold" mb={2}>
          {template && !isCopy ? "Edit Template" : "New Template"}
        </SuiTypography>
        {localError && (
          <SuiTypography
            variant="caption"
            display="block"
            mb={1}
            sx={{ color: "#c01616 !important", fontWeight: 500 }}
          >
            {localError}
          </SuiTypography>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SuiInput
              placeholder="Label (e.g. Full Quarter Jan-Mar)"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <SuiTypography variant="caption">From Month</SuiTypography>
            {monthSelect("from_month")}
          </Grid>
          <Grid item xs={6}>
            <SuiTypography variant="caption">To Month</SuiTypography>
            {monthSelect("to_month")}
          </Grid>
          {percentageInput("client_satisfaction_percentage", "Client Satisfaction %")}
          {percentageInput("professional_goals_percentage", "Professional Goals %")}
          {percentageInput("personal_goals_percentage", "Personal Goals %")}
          <Grid item xs={12}>
            <SuiBox display="flex" alignItems="center" gap={1}>
              <SuiTypography variant="caption" fontWeight="medium">
                Total
              </SuiTypography>
              <GoalPill color="info">{totalPercentage}%</GoalPill>
            </SuiBox>
          </Grid>
          <Grid item xs={12}>
            <SuiBox display="flex" gap={1} justifyContent="flex-end">
              <GoalButton variant="secondary" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </GoalButton>
              <GoalButton
                variant="primary"
                icon="check"
                loading={isSubmitting}
                onClick={handleSave}
                disabled={isSubmitting}
              >
                Save
              </GoalButton>
            </SuiBox>
          </Grid>
        </Grid>
      </SuiBox>
    </Card>
  );
}

export default GoalTemplateForm;
